import { Entity } from '@inter-ikea-core/core';
import {
  Conflict,
  ProductEntity,
  ProductEntityWithPosition,
  ShoppingItemWithPrice,
  VPCConfiguration,
  Wall
} from '..';

export const EVENT_TYPES = [
  'selected',
  'deselected',
  'history',
  'moveEnd',
  'load',
  'reset',
  'modelLoaded',
  'allModelsLoaded',
  'loadLegacyShoppingItems',
  'loadVpc',
  'loadVpcFailure',
  'loadVpcSuccess',
  'update',
  'conflict',
  'moving',
  'walls',
  'roomSize',
  'saveVpcStart',
  'saveVpcComplete',
  'solutionUpdateInProgress',
  'solutionUpdateDone',
  'unavailableProductsUpdated',
  'inScene',
  'undo',
  'redo',
  'duplicate',
  'helpLinesUpdated',
  'helpLinesConflictsResolved',
  'helpLinesShapeResizeFinished',
  'helpLinesShapeResize',
  'helpLinesShapeChange',
  'proppingAgeGroupUpdated',
  'loadWallShapeFromVpc',
  'tooManyLightsAdded',
  'enableNightMode',
  'placementAttempt',
  'saveDesignId',
  'loadDesignId'
] as const;
export type EventType = typeof EVENT_TYPES[number];

export enum ErrorMessages {
  NoFramesInScene = 'NoFramesInScene',
  NoDoorsInScene = 'NoDoorsInScene',
  ItemWontFit = 'ItemWontFit',
  LegsWontFit = 'LegsWontFit',
  HandlesTooBigForDoor = 'HandlesTooBigForDoor',
  PaxSlidingDoorOnCorner = 'PaxSlidingDoorOnCorner',
  PaxCornerFrameOpenCombination = 'PaxCornerFrameOpenCombination',
  PaxDoorAppliedButNotFittingCorner = 'PaxDoorAppliedButNotFittingCorner',
  PaxDoorHandlesOnCornerSolution = 'PaxDoorHandlesOnCornerSolution',
  PaxDoorHandlesOnSlidingDoors = 'PaxDoorHandlesOnSlidingDoors',
  PaxDoorHandlesOnDoorsIntegratedHandles = 'PaxDoorHandlesOnDoorsIntegratedHandles',
  PaxDoorHandlesOnDoorsMixedIncludedIntegratedHandles = 'PaxDoorHandlesOnDoorsMixedIncludedIntegratedHandles',
  PaxSlidingDoorApplicableOnSomeFrames = 'PaxSlidingDoorApplicableOnSomeFrames',
  NoRoomForOuterLights = 'NoRoomForOuterLights',
  AddingEketToBestaFrames = 'AddingEketToBestaFrames',
  AddingBestaToEketFrames = 'AddingBestaToEketFrames',
  AddingToMixedBestaEketFrames = 'AddingToMixedBestaEketFrames',
  AddingLightsToBestaSolidDoors = 'AddingLightsToBestaSolidDoors',
  AddingLightsToBestaMixedDoors = 'AddingLightsToBestaMixedDoors',
  AddingBestaTopPanelNoMatchingFrames = 'AddingBestaTopPanelNoMatchingFrames',
  PlatsaSlidingDoorsNotPossible = 'PlatsaSlidingDoorsNotPossible',
  PlatsaAddingDoorsToOpenFrames = 'PlatsaAddingDoorsToOpenFrames',
  PlatsaAddingHandlesToOpenFrames = 'PlatsaAddingHandlesToOpenFrames',
  PlatsaClothRailCollision = 'PlatsaClothRailCollision',
  SmastadAddingToFramesWithIncludedDoors = 'SmastadAddingToFramesWithIncludedDoors',
  SmastadAddingToMixedFramesWithIncludedDoors = 'SmastadAddingToMixedFramesWithIncludedDoors',
  SmastadAddingToPullOutFrames = 'SmastadAddingToPullOutFrames',
  SmastadAddingToMixedPullOutFrames = 'SmastadAddingToMixedPullOutFrames',
  IvarAddingDeskTo50DepthFrame = 'IvarAddingDeskTo50DepthFrame',
  IvarAddingDrawerWontFit = 'IvarAddingDrawerWontFit',
  BrorAddingWallSystemInteriorToRegularFrame = 'BrorAddingWallSystemInteriorToRegularFrame',
  BrorAddingRegularInteriorToWallSystemFrame = 'BrorAddingRegularInteriorToWallSystemFrame',
  BrorNoMatchingFramesForCabinets = 'BrorNoMatchingFramesForCabinets',
  BrorNoMatchingFramesForDrawers = 'BrorNoMatchingFramesForDrawers',
  BillyExtensionWontFit = 'BillyExtensionWontFit',
  BillyCornerFrameNoDoors = 'BillyCornerFrameNoDoors',
  BillyExtensionMaxTwo = 'BillyExtensionMaxTwo',
  BillyExtensionSmallFrame = 'BillyExtensionSmallFrame',
  BillyMoveFrameToCornerDeleteExtAndDoor = 'BillyMoveFrameToCornerDeleteExtAndDoor',
  BillyInteriorCollidesWithDoor = 'BillyInteriorCollidesWithDoor',
  ChangeColorMissingFramesMessage = 'changeColorMissingFramesMessage',
  BestaTopPanelsAbove150Message = 'BestaTopPanelsAbove150Message',
  BestaTopPanelsNotAddedToSomeFramesMessage = 'BestaTopPanelsNotAddedToSomeFramesMessage',
  LadmakareInteriorOnWideFramesOnly = 'LadmakareInteriorOnWideFramesOnly',
  SmastadExteriorAccessoryOnDoorsOnly = 'SmastadExteriorAccessoryOnDoorsOnly',
  LadmakareCabinetBlockedByOtherCabinet = 'LadmakareCabinetBlockedByOtherCabinet',
  BoaxelClothesRailRequiresShelf = 'BoaxelClothesRailRequiresShelf'
}

interface EventMap {
  placementAttempt: ErrorMessages;
  selected: ProductEntity | null;
  deselected: ProductEntity | null;
  history: void;
  moveEnd: void;
  moving: void;
  load: void;
  reset: void;
  modelLoaded: number;
  allModelsLoaded: void;
  loadLegacyShoppingItems: ShoppingItemWithPrice[];
  loadVpc: void;
  loadVpcFailure: string | undefined;
  loadVpcSuccess: void;
  update: ProductEntityWithPosition[];
  conflict: Conflict[];
  walls: Wall[];
  invalidPlacement: number[]; // entityIds
  roomSize: any;
  saveVpcStart: void;
  saveVpcComplete: string;
  solutionUpdateInProgress: void;
  solutionUpdateDone: void;
  unavailableProductsUpdated: void;
  inScene: Entity;
  undo: void;
  redo: void;
  duplicate: Entity;
  helpLinesUpdated: void;
  helpLinesConflictsResolved: void;
  helpLinesShapeResizeFinished: void;
  helpLinesShapeChange: void;
  helpLinesShapeResize: void;
  proppingAgeGroupUpdated: string;
  loadWallShapeFromVpc: VPCConfiguration;
  tooManyLightsAdded: void;
  enableNightMode: boolean;
  saveDesignId: string;
  loadDesignId: void;
}

type EventKey = keyof EventMap;
export type SubscribeCallback<E extends EventKey> = EventMap[E] extends void ? () => void : (args: EventMap[E]) => void;

export type ListenerMap = {
  [K in EventKey]: Set<SubscribeCallback<K>>;
};

export const CLOTH_RAIL_OUTSIDE_CONNECTOR_KEY = 'adjustable-cloth-rail-outside-right-';
export const CLOTH_RAIL_INSIDE_CONNECTOR_KEY = 'adjustable-cloth-rail-inside-left-';
export const CLOTH_RAIL_LEFT_FRAME_CONNECTOR_KEY = 'frame-connector-left';
export const CLOTH_RAIL_RIGHT_FRAME_CONNECTOR_KEY = 'adjustable-connector';
export const CLOTH_RAIL_ALIGN_SERIES_LEFT = 'align-point-series-left-';
export const CLOTH_RAIL_ALIGN_SERIES_RIGHT = 'align-point-series-right-';

// AST parser for prettier needs an update to handle newer syntax
// eslint-disable-next-line prettier/prettier
type EventEmitterKey<E extends EventKey> = `on${Capitalize<E>}`;
export type EventEmissionMap = {
  [E in EventKey as EventEmitterKey<E>]: SubscribeCallback<E>;
}
